<template>
  <v-container fluid>
    <v-alert v-if="error" type="error" dismissible @input="error=null">{{ error }}</v-alert>
    <v-progress-linear v-if="loading && !items.length" indeterminate/>

    <v-dialog v-model="jsonDialog" max-width="800px">
      <v-card>
        <v-card-title>
          Raw JSON
          <v-spacer/>
          <v-btn icon @click="jsonDialog=false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <pre style="font-size:12px; overflow:auto; max-height:70vh;">{{ jsonDialogContent }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-card flat v-if="customer">
      <v-card-title>
        <router-link :to="{name: 'Customer', params: {id: uid}}">
          <v-btn outlined class="mr-2">
            <v-icon left>mdi-arrow-left</v-icon>
            Customer
          </v-btn>
        </router-link>
        <div class="d-flex flex-fill align-content-space-around justify-center align-center text-center ">
          <div>
            Stripe History: {{ customer.first_name }} {{ customer.last_name }}
            <div v-if="unpaid" class="text-center">
              <v-chip v-if="unpaid.count" small outlined color="red" class="mr-1">
                <strong>{{ unpaid.count }} unpaid {{ unpaid.count === 1 ? 'invoice' : 'invoices' }}</strong>
              </v-chip>
              <v-chip v-if="unpaid.count" small outlined color="red">
                <strong>{{ formatCurrency(unpaid.amount) }} uncollected</strong>
              </v-chip>
              <span v-else class="caption text--secondary">No unpaid invoices</span>
              <div v-if="unpaid.uncollectible_count" class="caption text--secondary">
                {{ unpaid.uncollectible_count }} marked uncollectible
                ({{ formatCurrency(unpaid.uncollectible_amount) }}),
                {{ unpaid.open_count }} open ({{ formatCurrency(unpaid.open_amount) }})
              </div>
            </div>
            <div class="text-center caption d-flex align-content-space-around justify-space-around">
              <div v-if="customer.stats && customer.stats.refunds">Refunds: {{
                  formatCurrency(customer.stats.refunds)
                }}
              </div>
              <div v-if="customer.stats && customer.stats.credits">Credits: {{
                  formatCurrency(customer.stats.credits)
                }}
              </div>
            </div>
          </div>
        </div>
        <v-btn
          v-if="stripeId"
          outlined
          :href="`https://dashboard.stripe.com/${newStripeAccount}/customers/${stripeId}`"
          target="_blank"
          class="mr-2"
        >
          <v-icon left>mdi-open-in-new</v-icon>
          Stripe Customer
        </v-btn>
        <v-btn icon outlined :color="showDebugLinks ? 'primary' : ''" @click="showDebugLinks=!showDebugLinks">
          <v-icon>mdi-bug-outline</v-icon>
        </v-btn>
      </v-card-title>


      <v-card-text>
        <div class="caption text--secondary mb-2">
          <v-icon x-small>mdi-gesture-tap</v-icon>
          Click any row to see details and links.
        </div>
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          hide-default-footer
          disable-pagination
          sort-by="created"
          sort-desc
          :expanded.sync="expandedItems"
          item-key="id"
          @click:row="toggleExpand"
        >
          <template v-slot:item.created="{item}">
            {{ dateFormatted(item.created * 1000, {formatString: 'll HH:mm:ss'}) }}
          </template>

          <template v-slot:item._type="{item}">
            <v-chip small :color="typeColor(item)" dark>{{ typeLabel(item) }}</v-chip>
          </template>

          <template v-slot:item.amount="{item}">
            <span :class="getAmount(item) < 0 ? 'green--text' : ''">
              {{ formatCurrency(getAmount(item)) }}
            </span>
            <div v-if="item._type === 'invoice' && item.amount_due < item.total" class="text--secondary caption">
              ({{ formatCurrency(item.amount_due) }} due)
            </div>
          </template>

          <template v-slot:item.ending_balance="{item}">
            <span v-if="item.ending_balance != null" class="text--secondary">
              {{ formatCurrency(item.ending_balance) }}
            </span>
          </template>

          <template v-slot:item.description="{item}">
            {{ getDescription(item) }}
            <div v-if="getItemWeekDate(item)" class="text--secondary caption mt-1">
              Fed Order for week of {{ dateFormatted(getItemWeekDate(item), {formatString: 'D MMM, YYYY'}) }}
            </div>
            <div v-if="item._type === 'charge' && item.status === 'failed' && item.failure_message"
                 class="red--text caption mt-1" data-cy="charge-failure">
              {{ item.failure_message }}
            </div>
            <template
              v-if="item._type === 'balance_transaction' && item.credit_note && typeof item.credit_note === 'object'">
              <div v-if="item.credit_note.memo" class="text--secondary caption mt-1">
                <strong>Memo:</strong> {{ item.credit_note.memo }}
              </div>
              <div v-if="item.credit_note.reason" class="text--secondary caption">
                <strong>Reason:</strong> {{ item.credit_note.reason.replace(/_/g, ' ') }}
              </div>
            </template>
          </template>

          <template v-slot:expanded-item="{headers, item}">
            <td :colspan="headers.length" class="pl-12 pr-4 py-2 grey lighten-5">
              <div class="py-1">
                <v-btn v-if="getSecondaryLink(item)" outlined :href="getSecondaryLink(item).url" target="_blank"
                       class="mr-1">
                  <v-icon left>{{ getSecondaryLink(item).icon }}</v-icon>
                  {{ getSecondaryLink(item).title }}
                </v-btn>
                <v-btn outlined :href="getDashboardLink(item)" target="_blank" class="mr-1">
                  <v-icon left>mdi-open-in-new</v-icon>
                  Stripe {{ getDashboardLinkLabel(item) }}
                </v-btn>
                <v-btn v-if="getInvoiceComparisonRoute(item)" outlined :to="getInvoiceComparisonRoute(item)"
                       class="mr-1">
                  <v-icon left>mdi-file-compare</v-icon>
                  Fed Invoice Comparison
                </v-btn>
                <v-btn v-if="item._type === 'balance_transaction' && item._request_id" outlined
                       :href="getRequestLink(item)" target="_blank" class="mr-1">
                  <v-icon left>mdi-calendar-clock</v-icon>
                  Request
                </v-btn>
                <v-btn v-if="showDebugLinks && item._type === 'balance_transaction'" outlined :href="getEventLink(item)"
                       target="_blank" class="mr-1">
                  <v-icon left>mdi-history</v-icon>
                  Event
                </v-btn>
                <v-btn v-if="showDebugLinks" outlined @click.stop="showJson(item)">
                  <v-icon left>mdi-code-json</v-icon>
                  JSON
                </v-btn>
              </div>
              <v-simple-table v-if="chargeFailureRows(item).length" dense class="grey lighten-5 mt-2"
                              style="max-width:640px" data-cy="charge-outcome">
                <tbody>
                <tr v-for="row in chargeFailureRows(item)" :key="row.label">
                  <td class="text--secondary" style="width:180px">{{ row.label }}</td>
                  <td>{{ row.value }}</td>
                </tr>
                </tbody>
              </v-simple-table>

              <v-simple-table v-if="item._type === 'invoice'" dense class="grey lighten-5 mt-2" style="max-width:400px">
                <tbody>
                <tr v-for="row in getInvoiceSummary(item)" :key="row.label">
                  <td :class="row.bold ? 'font-weight-bold' : 'text--secondary'">{{ row.label }}</td>
                  <td class="text-right" :class="row.bold ? 'font-weight-bold' : (row.amount < 0 ? 'green--text' : '')">
                    {{ formatCurrency(row.amount) }}
                  </td>
                </tr>
                </tbody>
              </v-simple-table>

              <v-simple-table v-if="getExpandableLines(item).length > 0" dense class="grey lighten-5 mt-1">
                <thead>
                <tr>
                  <th class="text-left">Description</th>
                  <th class="text-right" style="width:60px">Qty</th>
                  <th class="text-right" style="width:110px">Unit Price</th>
                  <th class="text-right" style="width:110px">Amount</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="line in getExpandableLines(item)" :key="line.id">
                  <td>{{ line.description }}</td>
                  <td class="text-right">{{ line.quantity }}</td>
                  <td class="text-right">{{
                      line.quantity ? formatCurrency(Math.round(line.amount / line.quantity)) : ''
                    }}
                  </td>
                  <td class="text-right">{{ formatCurrency(line.amount) }}</td>
                </tr>
                </tbody>
              </v-simple-table>

              <div v-if="hasMoreLines(item)" class="d-flex align-center mt-1">
                <span class="caption text--secondary">
                  Only the first {{ getExpandableLines(item).length }} line items are shown.
                </span>
                <v-btn small outlined class="ml-2" :loading="loadingLines[item.id]"
                       @click.stop="loadAllLines(item)">
                  <v-icon left small>mdi-format-list-bulleted</v-icon>
                  Show all line items
                </v-btn>
              </div>
              <div v-else-if="allLines[item.id]" class="caption text--secondary mt-1">
                All {{ allLines[item.id].length }} line items shown.
              </div>
            </td>
          </template>

          <template v-slot:item.status="{item}">
            <div class="d-flex align-content-space-around justify-end align-end">
              <div>
                <v-chip small v-if="item._type === 'charge' && item.amount_refunded > 0" outlined
                        color="purple darken-1"
                        class="ml-1">
                  <strong>{{ item.amount_refunded === item.amount ? 'refunded' : 'partial refund' }}</strong>
                </v-chip>
                <v-chip small v-else-if="item.status" outlined :color="statusColor(item.status)">
                  <strong>{{ item.status }}</strong>
                </v-chip>

              </div>
            </div>
          </template>

        </v-data-table>

        <div class="text-center mt-4 pb-4">
          <v-btn
            v-if="hasMore"
            outlined
            :loading="loadingMore"
            @click="loadMore"
          >
            <v-icon left>mdi-history</v-icon>
            Load Older History
          </v-btn>
          <span v-if="!hasMore && items.length > 0" class="text--secondary caption">
            All history loaded ({{ items.length }} items)
          </span>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import api from '@/api';
import {dateFormatted, formatCurrency} from '@/store/utils';

// Fed moved to a new Stripe account on 2026-10-21. Customers exist on both (same
// ids); charges, invoices and credit notes stay on the account that made them,
// and a live Stripe object id carries the last ten characters of its account id,
// so a link goes to whichever account holds the object.
const OLD_STRIPE_ACCOUNT = 'acct_1B9PlYAhKDCZ2p2o'; // Fed App
const NEW_STRIPE_ACCOUNT = 'acct_1GjcI1D0KVemEdeG'; // NutritionAide Inc.
function stripeDashboardAccount(objectId) {
  const oldToken = OLD_STRIPE_ACCOUNT.slice(-10);
  return typeof objectId === 'string' && objectId.includes(oldToken) ? OLD_STRIPE_ACCOUNT : NEW_STRIPE_ACCOUNT;
}

export default {
  name: 'StripeCustomer',
  props: {uid: String},

  data() {
    return {
      newStripeAccount: NEW_STRIPE_ACCOUNT,
      customer: null,
      items: [],
      // open + uncollectible invoice summary, returned with the first history page
      unpaid: null,
      hasMore: false,
      cursors: {},
      loading: false,
      loadingMore: false,
      error: null,
      showDebugLinks: false,
      expandedItems: [],
      // full line items fetched on demand, keyed by row id
      allLines: {},
      loadingLines: {},
      jsonDialog: false,
      jsonDialogContent: '',
      headers: [
        {text: 'Date', value: 'created', width: 200},
        {text: 'Type', value: '_type', width: 140, sortable: false},
        {text: 'Amount', value: 'amount', width: 120},
        {text: 'Balance', value: 'ending_balance', width: 110, sortable: false},
        {text: 'Description', value: 'description', sortable: false},
        {text: 'Status', value: 'status', width: 110, sortable: false},
      ],
    };
  },

  computed: {
    invoiceById() {
      const map = {};
      for (const item of this.items) {
        if (item._type === 'invoice') map[item.id] = item;
      }
      return map;
    },

    stripeId() {
      if (!this.customer) return null;
      return (this.customer.stripe && this.customer.stripe.id) ||
        (this.customer.beforeDelete && this.customer.beforeDelete.stripe_id) ||
        null;
    },
  },

  mounted() {
    this.init();
  },

  watch: {
    uid() {
      this.items = [];
      this.unpaid = null;
      this.cursors = {};
      this.hasMore = false;
      this.error = null;
      this.expandedItems = [];
      this.allLines = {};
      this.loadingLines = {};
      this.init();
    },
  },

  methods: {
    dateFormatted,
    formatCurrency,

    async init() {
      this.loading = true;
      this.error = null;
      try {
        const r = await api.get('users/firebase/' + this.uid);
        this.customer = r.data;
        if (!this.stripeId) {
          this.error = 'Customer has no Stripe account linked';
          return;
        }
        const response = await api.get('v2/stripe/customer-history', {params: {customer_id: this.stripeId}});
        this.items = response.data.items;
        this.unpaid = response.data.unpaid || null;
        this.hasMore = response.data.has_more;
        this.cursors = response.data.cursors;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      this.loadingMore = true;
      try {
        const params = {customer_id: this.stripeId, ...this.cursors};
        const r = await api.get('v2/stripe/customer-history', {params});
        this.items = [...this.items, ...r.data.items];
        this.hasMore = r.data.has_more;
        this.cursors = r.data.cursors;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || 'Failed to load history';
      } finally {
        this.loadingMore = false;
      }
    },

    typeColor(item) {
      if (item._type === 'charge') return 'blue darken-1';
      if (item._type === 'refund') return 'purple darken-1';
      if (item._type === 'invoice') return 'green darken-1';
      if (item._type === 'balance_transaction') {
        return item.amount < 0 ? 'teal darken-1' : 'orange darken-1';
      }
      return 'grey';
    },

    typeLabel(item) {
      if (item._type === 'charge') return 'Payment';
      if (item._type === 'refund') return 'Refund';
      if (item._type === 'invoice') return 'Invoice';
      if (item._type === 'balance_transaction') {
        return item.amount < 0 ? 'Credit Given' : 'Credit Used';
      }
      return item._type;
    },

    getAmount(item) {
      if (item._type === 'invoice') return item.total;
      if (item._type === 'refund') return -item.amount;
      return item.amount;
    },

    getDescription(item) {
      if (item._type === 'charge') return item.description || item.statement_descriptor || '';
      if (item._type === 'refund') {
        const btDesc = item.balance_transaction && typeof item.balance_transaction === 'object'
          ? item.balance_transaction.description : null;
        return btDesc || (item.reason ? item.reason.replace(/_/g, ' ') : (item._charge_description || ''));
      }
      if (item._type === 'invoice') return item.description || item.number || '';
      if (item._type === 'balance_transaction') {
        const typeLabels = {
          applied_to_invoice: 'Applied to Invoice',
          unapplied_from_invoice: 'Unapplied from Invoice',
          credit_note: 'Credit Note',
          invoice_overpaid: 'Invoice Overpaid',
          invoice_too_large: 'Invoice Too Large',
          invoice_too_small: 'Invoice Too Small',
          adjustment: null,
        };
        const label = typeLabels[item.type] !== undefined
          ? typeLabels[item.type]
          : item.type.replace(/_/g, ' ');
        if (item.type === 'adjustment') {
          return item.description || 'No note';
        }
        // if the credit_note is expanded we show memo/lines inline below, so just show the type label here
        return label + (item.description ? ` — ${item.description}` : '') +
          (item.credit_note && item.credit_note.number ? ` (${item.credit_note.number})` : '');
      }
      return '';
    },

    // why a payment failed: what Stripe and the bank said, from the charge and its outcome
    chargeFailureRows(item) {
      if (item._type !== 'charge' || item.status !== 'failed') return [];
      const outcome = item.outcome || {};
      const card = (item.payment_method_details && item.payment_method_details.card) || {};
      const rows = [
        {label: 'Failure message', value: item.failure_message},
        {label: 'Failure code', value: item.failure_code},
        {label: 'Bank said', value: outcome.seller_message},
        {label: 'Reason', value: outcome.reason},
        {label: 'Outcome', value: outcome.type},
        {label: 'Network status', value: outcome.network_status},
        {label: 'Network decline code', value: outcome.network_decline_code},
        {label: 'Advice', value: outcome.advice_code},
        {label: 'Risk level', value: outcome.risk_level},
        {label: 'Card', value: card.last4 ? `${card.brand} ${card.funding || ''} ending ${card.last4}` : null},
      ];
      return rows.filter(row => row.value);
    },

    statusColor(status) {
      if (['paid', 'succeeded'].includes(status)) return 'green';
      if (['open', 'pending'].includes(status)) return 'orange';
      if (['void', 'failed', 'uncollectible'].includes(status)) return 'red';
      return 'grey';
    },

    getInvoiceSummary(item) {
      if (item._type !== 'invoice') return [];
      const rows = [];
      if (item.subtotal != null) rows.push({label: 'Subtotal', amount: item.subtotal});
      if (item.total_discount_amounts && item.total_discount_amounts.length) {
        // Build id->name map from top-level expanded discount(s)
        const discountNames = {};
        if (item.discount && typeof item.discount === 'object') {
          discountNames[item.discount.id] = item.discount.coupon?.name || item.discount.coupon?.id || null;
        }
        for (const da of item.total_discount_amounts) {
          const discId = typeof da.discount === 'string' ? da.discount : da.discount?.id;
          const name = discId ? discountNames[discId] : null;
          rows.push({label: name ? `Discount (${name})` : 'Discount', amount: -da.amount});
        }
      }
      if (item.tax) rows.push({label: 'Tax', amount: item.tax});
      if (item.total != null) rows.push({label: 'Total', amount: item.total});
      // credit applied from customer balance
      if (item.starting_balance && item.starting_balance < 0) {
        const creditApplied = Math.min(-item.starting_balance, item.total || 0);
        if (creditApplied > 0) rows.push({label: 'Credit Applied', amount: -creditApplied});
      }
      if (item.amount_credited) rows.push({label: 'Credit Notes', amount: -item.amount_credited});
      if (item.amount_due != null) rows.push({label: 'Amount Due', amount: item.amount_due, bold: true});
      return rows;
    },

    // The line item list embedded in the row, as Stripe returned it. Stripe caps
    // this at 10 entries and sets has_more when the document has more.
    getEmbeddedLines(item) {
      if (item._type === 'balance_transaction' &&
        item.credit_note && typeof item.credit_note === 'object') {
        return item.credit_note.lines || null;
      }
      if (item._type === 'invoice') {
        return item.lines || null;
      }
      return null;
    },

    getExpandableLines(item) {
      const sortByDescription = (a, b) => (a.description || '').localeCompare(b.description || '');
      const fetched = this.allLines[item.id];
      if (fetched) return fetched.slice().sort(sortByDescription);
      const lines = this.getEmbeddedLines(item);
      if (lines && lines.data) return lines.data.slice().sort(sortByDescription);
      return [];
    },

    hasMoreLines(item) {
      if (this.allLines[item.id]) return false;
      const lines = this.getEmbeddedLines(item);
      return !!(lines && lines.has_more);
    },

    async loadAllLines(item) {
      this.$set(this.loadingLines, item.id, true);
      try {
        const params = item._type === 'invoice'
          ? {invoice_id: item.id}
          : {credit_note_id: item.credit_note.id};
        const r = await api.get('v2/stripe/line-items', {params});
        this.$set(this.allLines, item.id, r.data.lines);
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || 'Failed to load line items';
      } finally {
        this.$set(this.loadingLines, item.id, false);
      }
    },

    toggleExpand(item) {
      const idx = this.expandedItems.findIndex(i => i.id === item.id);
      if (idx >= 0) {
        this.expandedItems.splice(idx, 1);
      } else {
        this.expandedItems.push(item);
      }
    },

    isExpanded(item) {
      return this.expandedItems.some(i => i.id === item.id);
    },

    getItemWeekDate(item) {
      if (item._type === 'invoice') {
        return item.metadata && item.metadata.week_date || null;
      }
      if (item._type === 'balance_transaction' && item.invoice) {
        const inv = this.invoiceById[item.invoice];
        return inv && inv.metadata && inv.metadata.week_date || null;
      }
      return null;
    },

    getInvoiceComparisonRoute(item) {
      let metadata = null;
      if (item._type === 'invoice') {
        metadata = item.metadata;
      } else if (item._type === 'balance_transaction' && item.invoice) {
        const inv = this.invoiceById[item.invoice];
        if (inv) metadata = inv.metadata;
      }
      if (!metadata || !metadata.week_date || !metadata.uid) return null;
      return {name: 'CustomerInvoiceComparison', query: {uid: metadata.uid, weekDate: metadata.week_date}};
    },

    getDashboardLinkLabel(item) {
      if (item._type === 'charge') return 'Payment';
      if (item._type === 'refund') return 'Refund';
      if (item._type === 'invoice') return 'Invoice';
      if (item._type === 'balance_transaction') {
        if (item.invoice) return 'Invoice';
        if (item.credit_note) return 'Credit Note';
        return 'Customer';
      }
      return 'View';
    },

    getDashboardLink(item) {
      const acct = stripeDashboardAccount(item.id || item._charge_id || item.invoice);
      if (item._type === 'charge') return `https://dashboard.stripe.com/${acct}/payments/${item.id}`;
      if (item._type === 'refund') return `https://dashboard.stripe.com/${acct}/payments/${item._charge_id}`;
      if (item._type === 'invoice') return `https://dashboard.stripe.com/${acct}/invoices/${item.id}`;
      if (item._type === 'balance_transaction') {
        if (item.invoice) return `https://dashboard.stripe.com/${acct}/invoices/${item.invoice}`;
        const cnId = item.credit_note?.id || item.credit_note;
        if (cnId) return `https://dashboard.stripe.com/${acct}/credit_notes/${cnId}`;
        return `https://dashboard.stripe.com/${acct}/customers/${this.stripeId}`;
      }
      return null;
    },

    // Returns {url, icon, title} for an optional secondary link, or null
    getSecondaryLink(item) {
      const acct = stripeDashboardAccount(item.id || item.invoice);
      if (item._type === 'charge' && item.receipt_url) {
        return {url: item.receipt_url, icon: 'mdi-receipt', title: 'Receipt'};
      }
      if (item._type === 'refund' && item._receipt_url) {
        return {url: item._receipt_url, icon: 'mdi-receipt', title: 'Receipt'};
      }
      if (item._type === 'invoice' && item.invoice_pdf) {
        return {url: item.invoice_pdf, icon: 'mdi-file-pdf-box', title: 'Invoice PDF'};
      }
      if (item._type === 'balance_transaction' && item.credit_note) {
        const pdf = item.credit_note?.pdf;
        if (pdf) return {url: pdf, icon: 'mdi-file-pdf-box', title: 'Credit Note PDF'};
        const cnId = item.credit_note?.id || item.credit_note;
        if (item.invoice && cnId) {
          // primary is invoice, secondary is credit note dashboard
          return {
            url: `https://dashboard.stripe.com/${acct}/credit_notes/${cnId}`,
            icon: 'mdi-file-document-outline',
            title: 'Credit Note'
          };
        }
      }
      return null;
    },

    getRequestLink(item) {
      const acct = stripeDashboardAccount(item.id); // request ids carry no account; the object's id says which made it
      return `https://dashboard.stripe.com/${acct}/workbench/logs?object=${item._request_id}&filtered=true`;
    },

    getEventLink(item) {
      const acct = stripeDashboardAccount(item.id);
      const params = new URLSearchParams();
      // pick the most specific event type based on what created this transaction
      const cn = item.credit_note;
      if (cn) {
        params.set('eventType', 'credit_note.*');
      } else if (item.invoice) {
        params.set('eventType', 'invoice.*');
      } else {
        // params.set('eventType', 'customer_balance_transaction.*');
      }
      params.set('resource', this.stripeId);
      if (item._event_id) params.set('eventId', item._event_id);
      return `https://dashboard.stripe.com/${acct}/workbench/events?${params.toString()}`;
    },

    showJson(item) {
      this.jsonDialogContent = JSON.stringify(item, null, 2);
      this.jsonDialog = true;
    },
  },
};
</script>
